import { SearchQueue } from 'lib-search-types';
import { memo, useMemo } from 'react';

import { dashboardApi } from 'apis/dashboard';
import { useDashboardRefetchEffect } from 'utils/dashboard';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { DragMoveEvent } from 'utils/useOrderedDragDrop';
import { useServerStorage } from 'utils/useServerStorage';

import { FavoriteDashboardFavorite } from '../../../favorite';
import { useQueueDashboardFavorite } from '../../utils/useQueueDashboardFavorite';

import { QueueDashboardOrganizationMetrics } from './components/organizationMetrics/queueDashboardOrganizationMetrics';
import { QueueDashboardQueue } from './components/queue/queueDashboardQueue';
import { QueueDashboardTable } from './components/table/queueDashboardTable';

type QueueDashboardQueuesProps = {
  /** The queues to display. */
  queues: readonly SearchQueue[];
  /** Handler called when the order of the queues changes. */
  onOrderChange?: (event: DragMoveEvent) => void;
};

export const QueueDashboardQueues = memo(function QueueDashboardQueues({
  queues,
  onOrderChange,
}: QueueDashboardQueuesProps): JSX.Element {
  const { params } = useAppSearchParams('/dashboard/queues');
  const storage = useServerStorage('user');
  const { layout } = storage.data.dashboard ?? {};
  const { favorite } = useQueueDashboardFavorite();

  // Stabilize the queue IDs so reordering alone doesn't trigger a metrics refetch.
  // We sort a copy so that the same set of IDs in any order produces the same key.
  const queueIdsKey = [...queues.map((q) => q.id)].sort().join();

  // The list of queue IDs, which only recomputes when the queues change, but not when reordering.
  const queueIds = useMemo(
    () => queues.map((q) => q.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queueIdsKey],
  );

  const { metrics, isSkeleton, refetch } =
    dashboardApi.useReadQueueDashboardMetricsQuery(
      { queueIds },
      {
        skip: !queues.length,
        selectFromResult: ({ data, isUninitialized, isFetching, ...rest }) => ({
          ...rest,
          metrics: data?.data.queue_metrics ?? [],
          isSkeleton: isUninitialized || isFetching,
        }),
      },
    );

  useDashboardRefetchEffect({ refetch });

  /**
   * The organization metrics are displayed when viewing "All queues" and not
   * searching for anything.
   */
  const showOrganizationMetrics = params.type === 'all' && !params.q;

  /**
   * The favorite metrics are displayed when viewing a favourite list, providing
   * an aggregated view of all queues in the list.
   */
  const showFavoriteMetrics =
    params.type === 'favourite' && !!params.id && !!favorite;

  const renderQueues = () => (
    <>
      {showOrganizationMetrics && <QueueDashboardOrganizationMetrics />}

      {showFavoriteMetrics && (
        <FavoriteDashboardFavorite
          favorite={favorite}
          queueMetrics={metrics}
          isSkeleton={isSkeleton}
        />
      )}

      {queues.map((queue, index) => (
        <QueueDashboardQueue
          key={queue.id}
          queue={queue}
          index={index}
          allQueueIds={queueIds}
          isSkeleton={isSkeleton}
          queueMetrics={metrics.find(({ id }) => id === queue.id)}
          onOrderChange={onOrderChange}
        />
      ))}
    </>
  );

  return layout === 'compact' ? (
    <QueueDashboardTable>{renderQueues()}</QueueDashboardTable>
  ) : (
    renderQueues()
  );
});
